/**
 * BlueprintBrowser — Lists available workflow templates.
 * Shows "My Templates" and "Global Templates" with Edit/Clone/Delete actions.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';
import WorkflowStarter from './WorkflowStarter';

const API_BASE = '/api/external/v1/workflows';

const BlueprintBrowser = ({ onEdit, onClone, onClose }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [tab, setTab] = useState('my'); // 'my' or 'global'
    const [activeTemplate, setActiveTemplate] = useState(null);

    const fetchTemplates = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiCall(`${API_BASE}/templates`);
            setTemplates(data.templates || []);
        } catch (err) {
            setError(err.message || 'Failed to fetch templates');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleDelete = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;
        try {
            await apiCall(`${API_BASE}/templates/${templateId}`, { method: 'DELETE' });
            setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        } catch (err) {
            alert('Failed to delete: ' + (err.message || 'Unknown error'));
        }
    };

    const handleClone = async (templateId) => {
        try {
            const data = await apiCall(`${API_BASE}/templates/${templateId}/clone`, {
                method: 'POST',
            });
            if (onClone) onClone(data.template);
            fetchTemplates();
        } catch (err) {
            alert('Failed to clone: ' + (err.message || 'Unknown error'));
        }
    };

    const myTemplates = templates.filter((t) => !t.is_global);
    const globalTemplates = templates.filter((t) => t.is_global);
    const displayed = tab === 'my' ? myTemplates : globalTemplates;

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            <div style={{
                width: '600px', maxHeight: '70vh', background: '#1E293B',
                borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 24px 48px rgba(0,0,0,0.4)', display: 'flex', flexDirection: 'column',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ color: '#F1F5F9', fontSize: '18px', fontWeight: 700, margin: 0 }}>
                        Workflow Templates
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94A3B8', cursor: 'pointer', fontSize: '20px' }}>✕</button>
                </div>

                {/* Tabs */}
                <div style={{ display: 'flex', padding: '0 24px', gap: '4px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {[
                        { id: 'my', label: `My Templates (${myTemplates.length})` },
                        { id: 'global', label: `Global Templates (${globalTemplates.length})` },
                    ].map((t) => (
                        <button
                            key={t.id}
                            onClick={() => setTab(t.id)}
                            style={{
                                padding: '10px 16px', fontSize: '13px', fontWeight: 600, border: 'none', cursor: 'pointer',
                                background: 'none', color: tab === t.id ? '#6366F1' : '#94A3B8',
                                borderBottom: tab === t.id ? '2px solid #6366F1' : '2px solid transparent',
                                transition: 'all 0.2s ease',
                            }}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#94A3B8' }}>Loading...</div>
                    ) : error ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#EF4444' }}>{error}</div>
                    ) : displayed.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#64748B' }}>
                            {tab === 'my' ? 'No templates yet. Create one!' : 'No global templates available.'}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {displayed.map((tmpl) => (
                                <div
                                    key={tmpl.id}
                                    style={{
                                        padding: '14px 16px', borderRadius: '10px',
                                        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        transition: 'all 0.2s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
                                >
                                    <div style={{ flex: 1 }}>
                                        <div style={{ color: '#F1F5F9', fontSize: '14px', fontWeight: 600 }}>{tmpl.name}</div>
                                        <div style={{ color: '#64748B', fontSize: '11px', marginTop: '2px' }}>
                                            {tmpl.nodes?.length || 0} nodes · v{tmpl.version || 1}
                                            {tmpl.updated_at && ` · ${new Date(tmpl.updated_at).toLocaleDateString()}`}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <ActionButton label="Start" color="#F59E0B" onClick={() => setActiveTemplate(tmpl)} />
                                        {tab === 'my' ? (
                                            <>
                                                <ActionButton label="Edit" color="#6366F1" onClick={() => onEdit(tmpl)} />
                                                <ActionButton label="Delete" color="#EF4444" onClick={() => handleDelete(tmpl.id)} />
                                            </>
                                        ) : (
                                            <ActionButton label="Clone" color="#10B981" onClick={() => handleClone(tmpl.id)} />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* WorkflowStarter Modal */}
            {activeTemplate && (
                <WorkflowStarter
                    template={activeTemplate}
                    onClose={() => setActiveTemplate(null)}
                    onStarted={(data) => {
                        setActiveTemplate(null);
                        alert(`Workflow started! Execution ID: ${data.execution_id}`);
                    }}
                />
            )}
        </div>
    );
};

const ActionButton = ({ label, color, onClick }) => (
    <button
        onClick={onClick}
        style={{
            padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
            background: `${color}1A`, border: `1px solid ${color}33`, color,
            cursor: 'pointer', transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => { e.target.style.background = `${color}33`; }}
        onMouseLeave={(e) => { e.target.style.background = `${color}1A`; }}
    >
        {label}
    </button>
);

export default BlueprintBrowser;
